import type * as api from "./api";
import { type CalendarDay, calendarDays, daysFrom, type SharePoint } from "./chart";
import { dateLabel } from "./format";

/** The visible days of the chart, as day offsets from now. */
export interface View {
  from: number;
  to: number;
}

export const DEFAULT_PAST = 28;
export const ALL_PAST = 7305;
export const MIN_SPAN = 3;
const RAMP_MAX_DAYS = 180;
const LEVEL_GAP_DAYS = 3;
const MINUTE = 1 / 1440;

/** The quick ranges of the page chart (SO-4320); "All" asks the API for its longest history. */
export const CONE_RANGES = [
  { key: "3m", short: "3m", label: "3 months", days: 91 },
  { key: "6m", short: "6m", label: "6 months", days: 182 },
  { key: "1y", short: "1y", label: "1 year", days: 365 },
  { key: "2y", short: "2y", label: "2 years", days: 730 },
  { key: "5y", short: "5y", label: "5 years", days: 1826 },
  { key: "all", short: "All", label: "All", days: ALL_PAST },
] as const;
export type ConeRange = (typeof CONE_RANGES)[number];

/** The history points of the cone, with their day offsets from now, in time order. */
export function historyDays(cone: Pick<api.FillCone, "history">, now: number): (api.ConePoint & { day: number })[] {
  return (cone.history ?? [])
    .map((point) => ({ ...point, day: daysFrom(point.at, now) }))
    .filter((point) => point.day <= 0)
    .sort((a, b) => a.day - b.day);
}

/** The days that the chart can show: from the oldest data to the last cone day. */
export function dataDomain(cone: Pick<api.FillCone, "days" | "history" | "past" | "backcast">, now: number): View {
  const to = cone.days[cone.days.length - 1]?.day ?? DEFAULT_PAST;
  const first = Math.min(cone.days[0]?.day ?? -DEFAULT_PAST, cone.backcast?.[0]?.day ?? 0, ...historyDays(cone, now).slice(0, 1).map((point) => point.day - 1));
  return { from: Math.max(first, -(cone.past ?? DEFAULT_PAST) - 1), to };
}

/** The words for a quick range longer than the data: "History starts Aug 27, 2026."; null when the data fills the range. */
export function historyNote(cone: Pick<api.FillCone, "days" | "history" | "backcast">, now: number, days: number, timeZone?: string): string | null {
  const first = Math.min(cone.days.find((day) => day.p50 !== null)?.day ?? 0, cone.backcast?.[0]?.day ?? 0, historyDays(cone, now)[0]?.day ?? 0);
  if (first <= 1 - days) return null;
  const date = dateAt(first, now, timeZone);
  return `History starts ${dateLabel(date)}, ${date.slice(0, 4)}.`;
}

/** The first view: 28 days back and the cone ahead, as before the zoom. */
export const defaultView = (domain: View): View => clampView({ from: -DEFAULT_PAST, to: domain.to }, domain);

/** The view of a quick range: that many days back, and the cone ahead. */
export const rangeView = (days: number, domain: View): View => clampView({ from: -days, to: domain.to }, domain);

/** Keep the view inside the domain and at least MIN_SPAN days wide. */
export function clampView(view: View, domain: View, minSpan = MIN_SPAN): View {
  const room = domain.to - domain.from;
  const span = Math.min(Math.max(view.to - view.from, Math.min(minSpan, room)), room);
  const from = Math.min(Math.max(view.from, domain.from), domain.to - span);
  return { from, to: from + span };
}

/** Zoom by `factor` (under 1 zooms in) and keep the day `at` at the same place on the screen. */
export function zoomView(view: View, at: number, factor: number, domain: View): View {
  const span = Math.max((view.to - view.from) * factor, MIN_SPAN);
  const share = (at - view.from) / Math.max(view.to - view.from, MINUTE);
  return clampView({ from: at - share * span, to: at - share * span + span }, domain);
}

/** Move the view by `days`, with the same span. */
export const panView = (view: View, days: number, domain: View): View => clampView({ from: view.from + days, to: view.to + days }, domain);

/** The view of a drag selection from day `a` to day `b`; a short drag gives MIN_SPAN days round its middle. */
export function selectView(a: number, b: number, domain: View): View {
  const from = Math.min(a, b);
  const to = Math.max(a, b);
  const middle = (from + to) / 2;
  const span = Math.max(to - from, MIN_SPAN);
  return clampView({ from: middle - span / 2, to: middle + span / 2 }, domain);
}

/** The day under an x position of the plot. */
export const dayAt = (px: number, left: number, right: number, view: View) => view.from + ((px - left) / Math.max(1, right - left)) * (view.to - view.from);

/** The x position of a day on the plot. */
export const xAt = (day: number, left: number, right: number, view: View) => left + ((day - view.from) / Math.max(MINUTE, view.to - view.from)) * (right - left);

/** The share of a line at `day`, from its two points round it; null outside the line or over a gap wider than `maxGap`. */
export function lineAt(points: readonly SharePoint[], day: number, maxGap = Infinity): number | null {
  if (!points.length || day < points[0].day - MINUTE || day > points[points.length - 1].day + MINUTE) return null;
  const next = points.findIndex((point) => point.day >= day);
  if (next <= 0) return points[Math.max(next, 0)].share;
  const a = points[next - 1];
  const b = points[next];
  if (b.day - a.day > maxGap) return null;
  return a.share + ((b.share - a.share) * (day - a.day)) / Math.max(MINUTE, b.day - a.day);
}

/** The straight back-cast lines between two pickups: from 0 after a pickup up to the next pickup. */
export function pickupRamps(points: readonly (api.ConePoint & { day: number })[], until: number): SharePoint[][] {
  const pickups = points.filter((point) => point.kind === "pickup" && point.day <= until + MINUTE);
  const ramps: SharePoint[][] = [];
  pickups.slice(1).forEach((pickup, index) => {
    const before = pickups[index];
    const gap = pickup.day - before.day;
    const read = points.filter((point) => point.kind === "level" && point.day > before.day && point.day < pickup.day).length;
    if (gap > MINUTE && gap <= RAMP_MAX_DAYS && read < 2) ramps.push([{ day: before.day, share: 0 }, { day: pickup.day, share: pickup.share }]);
  });
  return ramps;
}

export type CrossLine = "median" | "reading" | "back";

export interface Cross {
  share: number;
  line: CrossLine;
}

/** The lines that the crosshair reads: the median ahead, and the sensor readings or back-cast lines in the past. */
export interface CrossLines {
  median: SharePoint[];
  readings: SharePoint[];
  back: SharePoint[][];
}

/** The fill where the vertical crosshair at `day` meets the median, or in the past a reading or a back-cast line. */
export function crossAt(day: number, lines: CrossLines): Cross | null {
  const start = lines.median[0]?.day;
  if (start !== undefined && day >= start - MINUTE) {
    const share = lineAt(lines.median, day);
    if (share !== null) return { share, line: "median" };
  }
  const reading = lineAt(lines.readings, day, LEVEL_GAP_DAYS);
  if (reading !== null) return { share: reading, line: "reading" };
  for (const part of lines.back) {
    const share = lineAt(part, day);
    if (share !== null) return { share, line: "back" };
  }
  return null;
}

/** The median of the cone days that have one, as a line. */
export const medianLine = (days: readonly api.ConeDay[]): SharePoint[] =>
  days.filter((day) => day.p50 !== null).map((day) => ({ day: day.day, share: day.p50 as number }));

/** The local calendar date of a day offset from now. */
export const dateAt = (day: number, now: number, timeZone?: string): string => calendarDays(day, day, now, timeZone)[0].date;

export interface AxisTick {
  day: number;
  date: string;
  /** One line, or two (weekday over date). */
  lines: string[];
}

export interface TimeAxis {
  labels: AxisTick[];
  /** The days of the small ticks with no label. */
  minor: number[];
  /** The days of the larger ticks. */
  major: number[];
  weekends: boolean;
}

const LABEL_PX = 64;
const DAY_STEPS = [1, 2, 3];
const monthOf = (day: CalendarDay) => Number(day.date.slice(5, 7)) - 1;
const yearOf = (day: CalendarDay) => Number(day.date.slice(0, 4));
const monthName = (date: string) => new Date(`${date.slice(0, 7)}-15T12:00:00Z`).toLocaleDateString(undefined, { month: "short", timeZone: "UTC" });

/** The month label: the year on a January, else the month, with its year on the first label only. */
function monthLabel(day: CalendarDay, first: boolean): string {
  if (monthOf(day) === 0) return String(yearOf(day));
  return first ? `${monthName(day.date)} ${yearOf(day)}` : monthName(day.date);
}

/** The ticks and labels of the time axis for a view and a plot width: days, weeks, months or years (SO-4322). */
export function timeAxis(view: View, now: number, plotWidth: number, timeZone?: string): TimeAxis {
  const perDay = plotWidth / Math.max(MINUTE, view.to - view.from);
  const days = calendarDays(view.from, view.to, now, timeZone).filter((day) => day.start >= view.from - MINUTE && day.start <= view.to + MINUTE);
  const tick = (day: CalendarDay, lines: string[]): AxisTick => ({ day: day.start, date: day.date, lines });
  const dayStep = DAY_STEPS.find((n) => perDay * n >= LABEL_PX);
  if (dayStep !== undefined) {
    const shown = days.filter((day) => ((day.offset % dayStep) + dayStep) % dayStep === 0);
    const labels = shown.map((day) => tick(day, [dateLabel(day.date, "weekday"), dateLabel(day.date)]));
    return { labels, minor: days.map((day) => day.start), major: days.filter((day) => day.weekday === 1).map((day) => day.start), weekends: true };
  }
  if (perDay * 14 >= LABEL_PX) {
    const every = perDay * 7 >= LABEL_PX ? 1 : 2;
    const mondays = days.filter((day) => day.weekday === 1 && ((Math.floor(day.offset / 7) % every) + every) % every === 0);
    const two = perDay * 7 >= LABEL_PX && every === 1;
    const labels = mondays.map((day) => tick(day, two ? [dateLabel(day.date, "weekday"), dateLabel(day.date)] : [dateLabel(day.date)]));
    return { labels, minor: perDay >= 4 ? days.map((day) => day.start) : [], major: days.filter((day) => day.weekday === 1).map((day) => day.start), weekends: perDay >= 2.5 };
  }
  const months = days.filter((day) => day.date.endsWith("-01"));
  const step = [1, 2, 3, 6].find((n) => perDay * 30.4 * n >= LABEL_PX);
  if (step !== undefined) {
    const shown = months.filter((day) => monthOf(day) % step === 0);
    const labels = shown.map((day, index) => tick(day, [monthLabel(day, index === 0)]));
    return { labels, minor: perDay * 7 >= 6 ? days.filter((day) => day.weekday === 1).map((day) => day.start) : [], major: months.map((day) => day.start), weekends: false };
  }
  const years = months.filter((day) => monthOf(day) === 0);
  const every = [1, 2, 5, 10].find((n) => perDay * 365.25 * n >= LABEL_PX) ?? 10;
  const labels = years.filter((day) => yearOf(day) % every === 0).map((day) => tick(day, [String(yearOf(day))]));
  return { labels, minor: months.map((day) => day.start), major: years.map((day) => day.start), weekends: false };
}

/** The date under the crosshair: "Nov 5", with the year when it is not this year. */
export const crossDate = (date: string, now: number) => (date.slice(0, 4) === new Date(now).toISOString().slice(0, 4) ? dateLabel(date) : `${dateLabel(date)}, ${date.slice(0, 4)}`);

/** The words of the crosshair for the live text: "Thu Nov 5, 2026: most likely 72% full". */
export function crossWords(date: string, cross: Cross | null): string {
  const head = `${dateLabel(date, "both")}, ${date.slice(0, 4)}`;
  if (!cross) return `${head}: no fill line on this day`;
  const pct = `${Math.round(cross.share * 100)}% full`;
  if (cross.line === "median") return `${head}: most likely ${pct}`;
  return `${head}: ${cross.line === "reading" ? "sensor reading" : "estimated"} ${pct}`;
}
